const secureStream = require('./secureStream');
const { isPlaybackVerified, checkPlaybackStreams } = require('./probePlayback');

const browserPlatforms = new Set(['linux', 'windows', 'macos', 'ios', 'android', 'unknown']);

// The installed HTMLVideo engine only selects Hls.js for this exact MIME type.
// Public HLS sources may serve x-mpegURL or refuse cross-origin HEAD requests.
// Declare the format at the UI boundary; this does not bypass CORS or proxy media.
const prepareDirectBrowserStream = (stream, streamingServerURL) => {
    if (streamingServerURL || !stream?.url || stream?.behaviorHints?.proxyHeaders ||
        stream?.behaviorHints?.notWebReady || stream.infoHash || stream.ytId || stream.externalUrl) return stream;
    try {
        const url = new URL(stream.url);
        if (url.protocol !== 'https:' || !/\.m3u8$/i.test(url.pathname)) return stream;
        return {
            ...stream,
            behaviorHints: {
                ...stream.behaviorHints,
                proxyHeaders: { response: { 'content-type': 'application/vnd.apple.mpegurl' } },
            },
        };
    } catch (_) { return stream; }
};

// The native probe and the browser Player must agree for plain HTTPS video.
// A server's failed probe/HEAD request must not overturn measured video output.
// Keep server-dependent media, explicit conversion, shell and casting intact.
const canUseNativeBrowser = ({ stream, platformName, shellActive, casting, forceTranscoding, live }) => {
    if (!browserPlatforms.has(platformName) || shellActive || casting || forceTranscoding || live ||
        stream?.infoHash || stream?.ytId || stream?.externalUrl ||
        stream?.behaviorHints?.proxyHeaders || stream?.behaviorHints?.notWebReady) return false;
    try {
        const url = new URL(secureStream(stream)?.url);
        return url.protocol === 'https:' && !url.username && !url.password;
    } catch (_) { return false; }
};

async function resolvePlaybackServer(options, signal, dependencies = {}) {
    const configured = options.streamingServerURL;
    if (!configured || signal.aborted || !canUseNativeBrowser(options)) return configured;
    const verified = dependencies.isVerified || isPlaybackVerified;
    const check = dependencies.check || checkPlaybackStreams;
    try {
        if (verified(options.stream)) return null;
        // Direct/refreshed routes may lack the Details result. Test only the
        // explicitly selected source; cancellation never selects another one.
        const results = await check([options.stream], signal);
        return !signal.aborted && results[0]?.playbackVerified === true ? null : configured;
    } catch (_) { return configured; }
}

// Restrict compatibility transport to diagnosed HLS and Frost MP4 routes. Other media,
// shell, Cast and explicit transcoding keep their existing engine/server path.
async function prepareBrowserPlayback(options, signal, dependencies = {}) {
    const stream = options.stream;
    let relay = false;
    let mp4 = false;
    try {
        const url = new URL(stream?.url);
        mp4 = url.protocol === 'https:' && !url.username && !url.password &&
            url.hostname === 'froststream.cloutteam.com' && /^\/(movie|series)\/[a-f0-9]{64}$/.test(url.pathname) && !url.search &&
            browserPlatforms.has(options.platformName) && !options.shellActive && !options.casting &&
            !options.forceTranscoding && !options.live && !stream.infoHash && !stream.ytId && !stream.externalUrl && !stream.behaviorHints?.proxyHeaders;
        relay = mp4 || canUseNativeBrowser(options) && url.hostname === 'embedplayer2.xyz' && /\.m3u8$/i.test(url.pathname);
    } catch (_) { /* non-URL sources retain the existing path */ }
    if (!relay) {
        const server = await resolvePlaybackServer(options, signal, dependencies);
        return { stream: prepareDirectBrowserStream(stream, server), streamingServerURL: server };
    }
    const response = await (dependencies.fetcher || fetch)('/api/playback/hls/session', {
        method: 'POST', credentials: 'same-origin', signal,
        headers: { 'content-type': 'application/json' }, body: JSON.stringify({ url: stream.url }),
    });
    if (!response.ok) throw new Error('Playback transport unavailable');
    const payload = await response.json();
    if (typeof payload.url !== 'string' || !/^\/api\/playback\/hls\/[\w-]+$/.test(payload.url)) throw new Error('Invalid playback transport');
    if (mp4 && payload.contentType !== 'video/mp4') throw new Error('Unsupported playback transport');
    return {
        streamingServerURL: null,
        stream: {
            ...stream, url: new URL(payload.url, dependencies.origin || window.location.origin).href,
            behaviorHints: { ...stream.behaviorHints, ...(mp4 ? { notWebReady: false } : {}), proxyHeaders: { response: { 'content-type': mp4 ? 'video/mp4' : 'application/vnd.apple.mpegurl' } } },
        },
    };
}

// Compatibility only, never a playback verdict or source selection. Every
// other source goes straight to the existing engine without native preflight.
const needsHlsBrowserTransport = (options) => {
    if (!canUseNativeBrowser(options)) return false;
    try {
        const url = new URL(options.stream.url);
        return url.hostname === 'embedplayer2.xyz' && /\.m3u8$/i.test(url.pathname);
    } catch (_) { return false; }
};
async function prepareHlsBrowserPlayback(options, signal, dependencies = {}) {
    if (!needsHlsBrowserTransport(options)) return { stream: options.stream, streamingServerURL: options.streamingServerURL };
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    return prepareBrowserPlayback(options, signal, dependencies);
}

module.exports = { canUseNativeBrowser, resolvePlaybackServer, prepareDirectBrowserStream, prepareBrowserPlayback, needsHlsBrowserTransport, prepareHlsBrowserPlayback };
